import { supabase } from '@/lib/supabase';

export type AppointmentPractitionerRow = {
  practitioner_id: string;
  is_primary: boolean;
};

/** Primary first, then every other row. The first id is appointments.clinician_id. */
export function orderedAppointmentPractitionerIds(
  rows: readonly AppointmentPractitionerRow[],
  fallbackPrimary: string | null,
): string[] {
  const primary =
    rows.find((row) => row.is_primary && row.practitioner_id)?.practitioner_id ?? fallbackPrimary;
  const ids: string[] = [];
  if (primary) ids.push(primary);
  for (const row of rows) {
    if (!row.practitioner_id || ids.includes(row.practitioner_id)) continue;
    ids.push(row.practitioner_id);
  }
  if (ids.length === 0 && fallbackPrimary) return [fallbackPrimary];
  return ids;
}

/** True when the primary or the extra practitioners differ. */
export function practitionerSelectionChanged(
  current: readonly string[],
  saved: readonly string[],
): boolean {
  if ((current[0] ?? null) !== (saved[0] ?? null)) return true;
  const extras = (ids: readonly string[]) =>
    [...ids.slice(1).filter((id) => id && id !== ids[0])].sort();
  const next = extras(current);
  const previous = extras(saved);
  return next.length !== previous.length || next.some((id, index) => id !== previous[index]);
}

/** Non-primary rows to insert or delete so the table matches the selection. */
export function extraPractitionerDiff(input: {
  selectedIds: readonly string[];
  existing: readonly AppointmentPractitionerRow[];
}): { add: string[]; remove: string[] } {
  const primary = input.selectedIds[0] ?? null;
  const wanted = new Set(
    input.selectedIds.filter((id, index) => index > 0 && Boolean(id) && id !== primary),
  );
  const current = new Set(
    input.existing
      .filter((row) => !row.is_primary && row.practitioner_id !== primary)
      .map((row) => row.practitioner_id),
  );
  return {
    add: [...wanted].filter((id) => !current.has(id)),
    remove: [...current].filter((id) => !wanted.has(id)),
  };
}

/**
 * Keep extra practitioners on an appointment that already has its primary
 * clinician_id. One selected person does not need a write: the appointment
 * trigger already mirrors clinician_id. No schema change.
 */
export async function syncExtraAppointmentPractitioners(input: {
  appointmentId: string;
  clinicId: string;
  practitionerIds: readonly string[];
  startsAt: string;
  endsAt: string;
}): Promise<void> {
  const { data, error } = await supabase
    .from('appointment_practitioners')
    .select('practitioner_id, is_primary')
    .eq('appointment_id', input.appointmentId);
  if (error) throw error;

  const diff = extraPractitionerDiff({
    selectedIds: input.practitionerIds,
    existing: data ?? [],
  });

  if (diff.remove.length > 0) {
    const removed = await supabase
      .from('appointment_practitioners')
      .delete()
      .eq('appointment_id', input.appointmentId)
      .eq('is_primary', false)
      .in('practitioner_id', diff.remove);
    if (removed.error) throw removed.error;
  }

  if (diff.add.length > 0) {
    const inserted = await supabase.from('appointment_practitioners').insert(
      diff.add.map((practitionerId) => ({
        appointment_id: input.appointmentId,
        clinic_id: input.clinicId,
        practitioner_id: practitionerId,
        is_primary: false,
        starts_at: input.startsAt,
        ends_at: input.endsAt,
        occupies: true,
      })),
    );
    if (inserted.error) throw inserted.error;
  }
}
